import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPlanById, updatePlan } from "../../redux/slices/planSlice";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";


const EditPlan = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { singlePlan, loading } = useSelector((state) => state.plan);

    const [formData, setFormData] = useState({
        name: "",
        price: "",
        maxListings: "",
        maxLeads: "",
        durationDays: "",
        contact: "", // ✅ NEW FIELD
        verifiedBadge: "free",
        features: [""]
    });

    // ✅ Fetch plan
    useEffect(() => {
        dispatch(getPlanById(id));
    }, [dispatch, id]);

    // ✅ Set data into form
    useEffect(() => {
        if (singlePlan) {
            setFormData({
                name: singlePlan.name || "",
                price: singlePlan.price || "",
                maxListings: singlePlan.maxListings || "",
                maxLeads: singlePlan.maxLeads || "",
                durationDays: singlePlan.durationDays || "",
                contact: singlePlan.contact || "", // ✅ SET VALUE
                verifiedBadge: singlePlan.verifiedBadge || "free",
                features: singlePlan.features?.length
                    ? singlePlan.features
                    : [""]
            });
        }
    }, [singlePlan]);

    // 🔄 Handle input
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // 🔥 Feature change
    const handleFeatureChange = (index, value) => {
        const updated = [...formData.features];
        updated[index] = value;
        setFormData({ ...formData, features: updated });
    };

    // ➕ Add feature
    const addFeature = () => {
        setFormData({
            ...formData,
            features: [...formData.features, ""]
        });
    };

    // ❌ Remove feature
    const removeFeature = (index) => {
        const updated = formData.features.filter((_, i) => i !== index);
        setFormData({ ...formData, features: updated });
    };

    // 🚀 Submit Update
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.price) {
            Swal.fire("Error", "Name and Price are required", "error");
            return;
        }

        try {
            await dispatch(updatePlan({ id, formData })).unwrap();

            Swal.fire({
                title: "Updated 🎉",
                text: "Plan updated successfully",
                icon: "success"
            }).then(() => {
                navigate("/plans");
            });

        } catch (err) {
            Swal.fire("Error", err.message || "Update failed", "error");
        }
    };

    return (
        <div className="container mt-4">
            <div className="card shadow-lg border-0">

                {/* Header */}
                <div className="card-header bg-dark text-white">
                    <h4 className="mb-0 text-white">Edit Plan</h4>
                </div>

                {/* Body */}
                <div className="card-body">

                    {loading ? (
                        <p>Loading...</p>
                    ) : (

                        <form onSubmit={handleSubmit}>

                            <div className="row">

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Plan Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Price (₹)</label>
                                    <input
                                        type="number"
                                        name="price"
                                        className="form-control"
                                        value={formData.price}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Max Listings</label>
                                    <input
                                        type="number"
                                        name="maxListings"
                                        className="form-control"
                                        value={formData.maxListings}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Max Leads</label>
                                    <input
                                        type="number"
                                        name="maxLeads"
                                        className="form-control"
                                        value={formData.maxLeads}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* ✅ CONTACT FIELD */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Contact Limit</label>
                                    <input
                                        type="tel"
                                        name="contact"
                                        className="form-control"
                                        placeholder="Enter contact limit"
                                        value={formData.contact}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Verified Badge</label>
                                    <select
                                        name="verifiedBadge"
                                        className="form-control"
                                        value={formData.verifiedBadge}
                                        onChange={handleChange}
                                    >
                                        <option value="free">Free</option>
                                        <option value="basic">Basic</option>
                                        <option value="standard">Standard</option>
                                        <option value="premium">Premium</option>
                                    </select>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Duration (Days)</label>
                                    <input
                                        type="number"
                                        name="durationDays"
                                        className="form-control"
                                        value={formData.durationDays}
                                        onChange={handleChange}
                                    />
                                </div>

                            </div>

                            {/* 🔥 Features */}
                            <div className="mb-3">
                                <label className="form-label">Features</label>

                                {formData.features.map((feature, index) => (
                                    <div className="d-flex mb-2" key={index}>

                                        <input
                                            type="text"
                                            className="form-control me-2"
                                            value={feature}
                                            onChange={(e) => handleFeatureChange(index, e.target.value)}
                                        />

                                        <button
                                            type="button"
                                            className="btn btn-danger me-2"
                                            onClick={() => removeFeature(index)}
                                        >
                                            ❌
                                        </button>

                                        {index === formData.features.length - 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                onClick={addFeature}
                                            >
                                                ➕
                                            </button>
                                        )}

                                    </div>
                                ))}
                            </div>

                            <button type="submit" className="btn btn-primary w-100">
                                Update Plan
                            </button>

                        </form>

                    )}

                </div>
            </div>
        </div>
    );
};

export default EditPlan;